import { chatDrawerSelectors } from 'ducks/chatDrawer'
import { wsSelectors } from 'ducks/websocket'
import { keyframes, styled } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useChatContext } from './ChatContext'
import { useChatSuggestionsPreferences } from './hooks/useChatSuggestionsPreferences'
import { useMessageSuggestions } from './hooks/useMessageSuggestions'

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(4px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

const CompactContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
  padding: '8px 16px',
  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
  backgroundColor: 'rgb(249, 250, 251)',
  overflowX: 'auto',
  scrollbarWidth: 'thin',
  animation: `${fadeIn} 0.3s ease-out`,
  '&::-webkit-scrollbar': {
    height: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '2px',
  },
})

const slideIn = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-8px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
`

const CompactSuggestionButton = styled('button')<{ animationDelay?: number; disabled?: boolean }>(
  ({ animationDelay = 0, disabled = false }) => ({
    padding: '6px 12px',
    borderRadius: '16px',
    border: disabled ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(23, 81, 208, 0.2)',
    backgroundColor: disabled ? 'rgba(245, 245, 245, 0.98)' : 'rgba(255, 255, 255, 0.98)',
    color: disabled ? 'rgba(0, 0, 0, 0.4)' : '#1751D0',
    fontSize: '12px',
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: 'all 0.2s ease',
    animation: `${slideIn} 0.3s ease-out ${animationDelay}s both`,
    opacity: disabled ? 0.6 : 1,
    pointerEvents: disabled ? 'none' : 'auto',
    '&:hover': {
      backgroundColor: disabled ? 'rgba(245, 245, 245, 0.98)' : 'rgba(23, 81, 208, 0.05)',
      borderColor: disabled ? 'rgba(0, 0, 0, 0.1)' : 'rgba(23, 81, 208, 0.3)',
    },
    '&:active': {
      transform: disabled ? 'none' : 'scale(0.98)',
    },
  })
)

const COMPACT_SUGGESTIONS_LIMIT = 3

export const CompactSuggestions: React.FC = () => {
  const suggestionKeys = useMessageSuggestions()
  const translate = useTranslate()
  const websocketAvailable = useSelector(wsSelectors.getAvailable)
  const messages = useSelector((state: any) => chatDrawerSelectors.getChatDrawerMessages(state))
  const { setMessageText, onSubmit, messageText, isAwaitingResponse } = useChatContext()
  const { preferences } = useChatSuggestionsPreferences()
  const [animationKey, setAnimationKey] = useState(0)
  const prevSuggestionKeysRef = useRef<string>('')
  const pendingSuggestionRef = useRef<string | null>(null)

  const suggestions = useMemo(() => {
    return suggestionKeys.slice(0, COMPACT_SUGGESTIONS_LIMIT).map((key) => translate(key))
  }, [suggestionKeys, translate])

  const suggestionKeysString = useMemo(() => suggestionKeys.join('|'), [suggestionKeys])

  useEffect(() => {
    if (prevSuggestionKeysRef.current !== suggestionKeysString && prevSuggestionKeysRef.current !== '') {
      setAnimationKey((prev) => prev + 1)
    }
    prevSuggestionKeysRef.current = suggestionKeysString
  }, [suggestionKeysString])

  // Submit when messageText matches the pending suggestion
  useEffect(() => {
    if (pendingSuggestionRef.current && messageText === pendingSuggestionRef.current) {
      pendingSuggestionRef.current = null
      onSubmit()
    }
  }, [messageText, onSubmit])

  if (!preferences.showCompactSuggestions || suggestions.length === 0 || !websocketAvailable || messages.length === 0) {
    return null
  }

  const handleSuggestionClick = (suggestion: string) => {
    if (isAwaitingResponse) return
    pendingSuggestionRef.current = suggestion
    setMessageText(suggestion)
  }

  const containerKey = `${suggestionKeysString}-${animationKey}`

  return (
    <CompactContainer key={containerKey}>
      {suggestions.map((suggestion, index) => (
        <CompactSuggestionButton
          key={`${suggestion}-${index}-${animationKey}`}
          animationDelay={index * 0.05}
          disabled={isAwaitingResponse}
          onClick={() => handleSuggestionClick(suggestion)}
        >
          {suggestion}
        </CompactSuggestionButton>
      ))}
    </CompactContainer>
  )
}
